import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Account, Tenant, Voucher } from 'src/models/account.model';
import { AuthService } from 'src/services/auth.service';
import { TenantService } from 'src/services/tenant.service';
import { VoucherService } from 'src/services/voucher.service';

interface TenantSession {
  tenant_id: string;
  account_key: string;
  store_name: string;
  tenant_name: string;
  username: string;
  logged_in_at: Date;
  expires_at: Date;
}

@Component({
  selector: 'app-voucher-list',
  templateUrl: './voucher-list.component.html',
  styleUrls: ['./voucher-list.component.css']
})
export class VoucherListComponent implements OnInit, OnDestroy {

  tenantSession: TenantSession | null = null;
  account: Account | null = null;
  tenant: Tenant | null = null;
  isLoading: boolean = true;

  vouchers: Voucher[] = [];
  filteredVouchers: Voucher[] = [];
  voucherSubscription: Subscription;

  filterStatus: string = 'all';
  searchCode: string = '';

  // Voucher check
  checkingVoucher: boolean = false;
  checkedVoucher: Voucher | null = null;
  checkMessage: string = '';

  // Make Math available to template
  Math = Math;

  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalVouchers: number = 0;

  constructor(
    private router: Router,
    private authService: AuthService,
    private tenantService: TenantService,
    private voucherService: VoucherService
  ) {}

  async ngOnInit() {
    await this.loadData();
  }

  ngOnDestroy() {
    if (this.voucherSubscription) {
      this.voucherSubscription.unsubscribe();
    }
  }

  async loadData() {
    this.isLoading = true;

    const sessionStr = localStorage.getItem('tenant_session');
    if (!sessionStr) {
      this.router.navigate(['/']);
      return;
    }

    this.tenantSession = JSON.parse(sessionStr);

    try {
      this.account = await this.authService.getAccountByKey(this.tenantSession.account_key);

      if (this.account) {
        this.tenant = this.tenantService.getTenantById(this.account, this.tenantSession.tenant_id);

        // Check and update expired vouchers in Firebase
        await this.voucherService.checkAndUpdateExpiredVouchers(this.tenantSession.tenant_id);

        // Subscribe to vouchers
        this.voucherSubscription = this.voucherService
          .getVouchersByTenant(this.tenantSession.tenant_id)
          .subscribe(vouchers => {
            this.vouchers = vouchers;
            this.applyFilters();
            this.isLoading = false;
          });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.isLoading = false;
    }
  }

  applyFilters() {
    let filtered = [...this.vouchers];

    // Check and update expired vouchers
    const now = new Date();
    filtered = filtered.map(v => {
      if (v.status === 'active') {
        const expiresAt = v.expires_at instanceof Date
          ? v.expires_at
          : new Date((v.expires_at as any).seconds * 1000);
        if (now > expiresAt) {
          return { ...v, status: 'expired' as const };
        }
      }
      return v;
    });

    // Filter by status
    if (this.filterStatus !== 'all') {
      filtered = filtered.filter(v => v.status === this.filterStatus);
    }

    // Filter by search code
    if (this.searchCode.trim()) {
      const search = this.searchCode.toLowerCase().trim();
      filtered = filtered.filter(v =>
        v.voucher_code.toLowerCase().includes(search)
      );
    }

    this.filteredVouchers = filtered;
    this.totalVouchers = filtered.length;
  }

  onFilterChange() {
    this.currentPage = 1;
    this.applyFilters();
  }

  onSearchChange() {
    this.currentPage = 1;
    this.applyFilters();
  }

  getPaginatedVouchers(): Voucher[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredVouchers.slice(startIndex, endIndex);
  }

  getTotalPages(): number {
    return Math.ceil(this.filteredVouchers.length / this.itemsPerPage);
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.getTotalPages()) {
      this.currentPage = page;
    }
  }

  clearSearch() {
    this.searchCode = '';
    this.applyFilters();
  }

  formatDate(date: Date | any): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date.seconds * 1000);
    return d.toLocaleString();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'used':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blueGray-100 text-blueGray-800';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'active':
        return 'fas fa-check-circle';
      case 'used':
        return 'fas fa-check-double';
      case 'expired':
        return 'fas fa-times-circle';
      default:
        return 'fas fa-question-circle';
    }
  }

  goToGenerate() {
    this.router.navigate([`/tenant/${this.tenantSession.account_key}/generate`]);
  }

  getStats() {
    const stats = {
      total: this.vouchers.length,
      active: 0,
      used: 0,
      expired: 0
    };

    const now = new Date();
    this.vouchers.forEach(v => {
      if (v.status === 'used') {
        stats.used++;
      } else if (v.status === 'expired') {
        stats.expired++;
      } else {
        const expiresAt = v.expires_at instanceof Date
          ? v.expires_at
          : new Date((v.expires_at as any).seconds * 1000);
        if (now > expiresAt) {
          stats.expired++;
        } else {
          stats.active++;
        }
      }
    });

    return stats;
  }

  async checkVoucher(voucherCode: string) {
    if (!voucherCode || this.checkingVoucher) return;

    this.checkingVoucher = true;
    this.checkedVoucher = null;
    this.checkMessage = '';

    try {
      const result = await this.voucherService.checkVoucherStatus(voucherCode);
      this.checkedVoucher = result.voucher;
      this.checkMessage = result.message;

      // If status was updated, the subscription will automatically refresh the list
    } catch (error) {
      console.error('Error checking voucher:', error);
      this.checkMessage = 'Error checking voucher status';
    } finally {
      this.checkingVoucher = false;
    }
  }

  clearCheck() {
    this.checkedVoucher = null;
    this.checkMessage = '';
  }
}
